
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  useQuery,
  useQueryClient,
  type QueryKey,
  type UseQueryResult,
} from '@tanstack/react-query';

import type { ApiError } from '../api/client';
import { tasksApi } from '../api/endpoints';
import type { Id, ToolTask, ToolTaskKind } from '../types/api';
import { isTaskFinished } from '../types/api';
import { pollIntervalFrom, pollWhileTaskRuns, queryKeys } from './keys';

export interface ToolTaskOptions {
  enabled?: boolean;
  invalidateOnFinish?: readonly QueryKey[];
}

export function useToolTask(
  taskId: Id | undefined,
  options: ToolTaskOptions = {},
): UseQueryResult<ToolTask, ApiError> {
  const qc = useQueryClient();
  const query = useQuery<ToolTask, ApiError>({
    queryKey: queryKeys.task(taskId ?? ''),
    queryFn: () => tasksApi.get(taskId as Id),
    enabled: Boolean(taskId) && (options.enabled ?? true),
    refetchInterval: (q) => {
      if (q.state.status === 'error') return false;
      const data = q.state.data;
      return pollWhileTaskRuns(data?.status, data?.poll_after_seconds);
    },
    refetchIntervalInBackground: true,
    retry: (failureCount, error) => !error.isNotFound && failureCount < 2,
  });

  const keysRef = useRef<readonly QueryKey[]>(options.invalidateOnFinish ?? []);
  keysRef.current = options.invalidateOnFinish ?? [];

  const handled = useRef<string | null>(null);
  const status = query.data?.status;
  useEffect(() => {
    if (!taskId || !isTaskFinished(status)) return;
    const stamp = `${taskId}:${status}`;
    if (handled.current === stamp) return;
    handled.current = stamp;
    for (const key of keysRef.current) {
      void qc.invalidateQueries({ queryKey: key });
    }
  }, [taskId, status, qc]);

  return query;
}

export function useToolTasks(
  kind?: ToolTaskKind,
  options: { enabled?: boolean } = {},
): UseQueryResult<ToolTask[], ApiError> {
  return useQuery<ToolTask[], ApiError>({
    queryKey: queryKeys.tasks(kind),
    queryFn: () => tasksApi.list(kind),
    enabled: options.enabled ?? true,
    refetchInterval: (q) => {
      const items = q.state.data;
      if (!items) return pollIntervalFrom(undefined);
      const running = items.filter((t) => !isTaskFinished(t.status));
      if (running.length === 0) return false;
      return pollIntervalFrom(running[0]?.poll_after_seconds);
    },
    refetchIntervalInBackground: false,
  });
}

export interface TaskTracker {
  task: UseQueryResult<ToolTask, ApiError>;
  taskId: Id | undefined;
  track: (taskId: Id) => void;
  reset: () => void;
  isRunning: boolean;
}

export function useTaskTracker(invalidateOnFinish: readonly QueryKey[] = []): TaskTracker {
  const [taskId, setTaskId] = useState<Id | undefined>(undefined);
  const task = useToolTask(taskId, { invalidateOnFinish });
  const reset = useCallback(() => setTaskId(undefined), []);
  return {
    task,
    taskId,
    track: setTaskId,
    reset,
    isRunning: Boolean(taskId) && !isTaskFinished(task.data?.status) && !task.isError,
  };
}

export function runningTasks(tasks: readonly ToolTask[] | undefined): ToolTask[] {
  return (tasks ?? []).filter((task) => !isTaskFinished(task.status));
}

export function taskProgressPct(task: ToolTask | undefined): number | null {
  const progress = task?.progress;
  if (!progress) return null;
  const total = Number(progress.total);
  const done = Number(progress.done ?? progress.processed ?? progress.completed);
  if (!Number.isFinite(total) || total <= 0 || !Number.isFinite(done)) return null;
  return Math.min(100, Math.round((done / total) * 100));
}
